import { ApiError } from "@/shared/api/httpClient";
import { NetworkError, RequestTimeoutError } from "@/shared/api/requestTimeout";
import { humanizeInternalTerms } from "@/shared/lib/internalCodes";
import { maskVendor } from "@/shared/lib/vendorMask";
import { MODULE_PAUSED_TITLE, moduleBlockReasonFromDetails } from "@/shared/lib/forbiddenReason";

/**
 * 全站统一错误码表 + 报错人话化(0926 修复波 E)。
 *
 * ★为什么有这张表:后端 ErrorCode.java 117 个码,console 此前只在 poster-errors 里认了 19 个
 *   (其中 QUOTA_EXCEEDED / BILLING_TICKET_INSUFFICIENT 等 8 个后端根本不发),其余一律落进
 *   兜底标题「请求失败 (XXX_CODE)」—— 裸码直接怼在用户眼前;积分不足三个码
 *   (INSUFFICIENT_CREDITS / POSTER_INSUFFICIENT_CREDITS / MODULE_QUOTA_EXCEEDED)一个都没配。
 *
 * ★口径:
 *   · 标题 = 这张表的中文短句(说「发生了什么」);
 *   · 描述 = 后端 message 原文(后端文案本身已是人话,且带着真数字——前端不写第二份数字),
 *     有 `hint` 的再接一句「下一步怎么办」;
 *   · 未知码:标题「操作没有成功」,描述用后端原文,错误码/排查号放在描述末行小字里,方便报障;
 *   · 断网 / 超时 / 网关 502·504 各给人话(见 CLIENT_ERROR_COPY)。
 *
 * ★守卫:`apiErrorCodes.snapshot.json`(由 `scripts/sync-error-codes.mjs <ErrorCode.java>` 生成)
 *   与 API_ERROR_COPY 双向相等,见 apiErrors.test.ts。后端加码 → 重跑脚本 → 测试红 → 在这里配标题。
 */
export interface ErrorCopy {
  /** toast 标题:中文短句,不许出现裸码。 */
  title: string;
  /** 下一步怎么办(可选);接在后端 message 后面。 */
  hint?: string;
}

const RECHARGE_HINT = "请联系管理员充值后再试";
const RETRY_LATER = "请稍后重试";
const REFRESH_HINT = "刷新页面后再试";
const RELOGIN_HINT = "请重新登录";

/** 后端 ErrorCode.java 的全部码(键集合 = apiErrorCodes.snapshot.json,测试双向断言)。 */
export const API_ERROR_COPY: Record<string, ErrorCopy> = {
  // ── 通用客户端错误 ──
  VALIDATION_ERROR: { title: "填写的内容有误" },
  BAD_REQUEST: { title: "请求有误" },
  UNAUTHORIZED: { title: "登录已失效", hint: RELOGIN_HINT },
  // B1 0927:SESSION_KICKED 一码多因(禁用 / 停用 / 改密 / 企微解绑 / 别处登录),这里没有 reason → 中性句;
  // 按原因分句在登录页横幅(features/auth/model/kickedReason)。
  SESSION_KICKED: { title: "登录失效", hint: "登录已失效,请重新登录" },
  FORBIDDEN: { title: "没有权限", hint: "如需此权限请联系管理员" },
  NOT_FOUND: { title: "内容不存在", hint: "可能已被删除,刷新页面看看" },
  CONFLICT: { title: "状态已变化", hint: REFRESH_HINT },
  CONFIG_INCOMPLETE: { title: "配置还不完整", hint: "请联系管理员补全配置" },
  TOO_MANY_REQUESTS: { title: "操作太频繁", hint: "请稍等片刻再试" },

  // ── 下游 / 上游 ──
  AI_SERVICE_ERROR: { title: "AI 服务出错了", hint: RETRY_LATER },
  UPSTREAM_INVALID_RESPONSE: { title: "上游服务返回异常", hint: RETRY_LATER },
  UPSTREAM_API_ERROR: { title: "上游服务调用失败", hint: RETRY_LATER },
  STORAGE_ERROR: { title: "文件存储暂时不可用", hint: RETRY_LATER },
  MESSAGE_QUEUE_ERROR: { title: "任务排队服务暂时不可用", hint: RETRY_LATER },

  // ── 海报 / 长图 ──
  POSTER_QUOTA_EXCEEDED: { title: "海报额度已用完", hint: "请联系管理员升级套餐" },
  POSTER_TASK_NOT_FOUND: { title: "任务不存在", hint: "可能已被删除,刷新列表看看" },
  POSTER_TASK_NOT_OWNED: { title: "无权查看这个任务" },
  RESOURCE_NOT_OWNED: { title: "无权访问" },
  // ★长图被 ai 侧硬钳拒掉(超长/占位内容/brand 非法)走这个码,「超长」等原因在描述里由原文兜着
  POSTER_INVALID_PARAMS: { title: "参数被拒" },
  POSTER_TASK_NOT_CANCELLABLE: { title: "无法取消" },
  // 两层 413 各说各的数字:这条是后端业务闸(10MB),PAYLOAD_TOO_LARGE 是边缘 nginx
  POSTER_UPLOAD_TOO_LARGE: { title: "图片太大" },
  POSTER_UPLOAD_BAD_FORMAT: { title: "图片格式不支持" },
  POSTER_AI_SERVICE_DOWN: { title: "AI 服务暂不可用", hint: "可稍后重试,或换个模型" },
  POSTER_RECOMPOSE_LOCKED: { title: "正在重新排版", hint: "请等这次完成后再操作" },
  POSTER_KB_EMPTY: { title: "知识库还是空的" },
  POSTER_PROVIDER_REJECTED: { title: "图像服务拒绝了这次请求", hint: "可调整描述或换个模型再试" },
  POSTER_CALLBACK_UNAUTHORIZED: { title: "回调鉴权失败" },
  POSTER_NO_BASE_IMAGE: { title: "这一版没有可编辑的底图" },
  POSTER_BRAND_ASSET_MISSING: { title: "品牌素材读不到了" },
  POSTER_TEMPLATE_INVALID_TIER: { title: "分辨率不被支持" },
  POSTER_TEMPLATE_INVALID_RATIO: { title: "比例不被支持", hint: "请检查模型与比例的组合" },
  POSTER_TEMPLATE_DELETE_NOT_ARCHIVED: { title: "请先归档再删除" },
  // 数字(上限 30)在后端单源,这里不写第二份
  POSTER_TOO_MANY_IN_FLIGHT: { title: "并发已满" },

  // ── 积分 / 额度 / 套餐 ──
  POSTER_INSUFFICIENT_CREDITS: { title: "积分不够这一单", hint: RECHARGE_HINT },
  INSUFFICIENT_CREDITS: { title: "积分不足", hint: RECHARGE_HINT },
  PLAN_FIELD_LOCKED: { title: "该字段不可修改" },
  MODULE_NOT_ENABLED: { title: "该功能未开通", hint: "请联系管理员开通" },
  MODULE_QUOTA_EXCEEDED: { title: "功能额度已用完" },
  // 席位硬挡(修复波 0926):后端 message 已带「已用 N / 上限 M」,这里只给标题
  SEAT_LIMIT_EXCEEDED: { title: "席位已满" },
  // 企微解绑被拒(修复波 0926 第八批 Z1):纯企微账号,需先重置密码;用户管理页据此码亮「去重置密码」
  WECOM_ONLY_UNBIND_REFUSED: { title: "需先重置密码" },

  // ── 报价单(报价即合同)──
  QUOTE_EXPIRED: { title: "报价已过期" },
  QUOTE_ALREADY_USED: { title: "报价单已被使用" },
  QUOTE_REQUIRED: { title: "缺少报价单", hint: REFRESH_HINT },

  // ── 视频 ──
  VIDEO_TASK_NOT_FOUND: { title: "视频任务不存在", hint: "可能已被删除,刷新列表看看" },
  VIDEO_ASSET_NOT_FOUND: { title: "视频素材不存在" },
  VIDEO_TEMPLATE_NOT_FOUND: { title: "视频模板不存在" },
  VIDEO_INVALID_PARAMS: { title: "视频参数有误" },
  VIDEO_UNSUPPORTED_PROVIDER: { title: "所选模型不可用" },
  VIDEO_UNSUPPORTED_RESOLUTION: { title: "分辨率不被支持" },
  VIDEO_UNSUPPORTED_RATIO: { title: "画面比例不被支持" },
  VIDEO_UNSUPPORTED_DURATION: { title: "时长不被支持" },
  VIDEO_UNSUPPORTED_MODE: { title: "生成方式不被支持" },
  VIDEO_REFERENCE_IMAGE_REQUIRED: { title: "缺少首帧图" },
  VIDEO_QUOTA_EXCEEDED: { title: "视频额度已用完", hint: "请联系管理员升级套餐" },
  // 1104 口径:后端不再按「同时在做」拒,收下排队;这个码 = 本户**排队上限**满(默认 20),
  //   描述用后端原文(带真数字:「排队中的视频已有 N 单(上限 20)…」)。
  VIDEO_TOO_MANY_IN_FLIGHT: { title: "排队中的视频已达上限" },
  VIDEO_AI_SERVICE_DOWN: { title: "AI 服务暂不可用", hint: RETRY_LATER },
  VIDEO_INPUT_REJECTED: { title: "视频请求被拒绝" },
  VIDEO_TASK_NOT_CANCELLABLE: { title: "无法取消" },
  VIDEO_BLOCKED_BY_SAFETY: { title: "内容未通过安全检测", hint: "请调整文案或素材后再试" },
  // 1011 算法备案:敏感词库拦截(输入 / 文本输出),api CONTENT_SENSITIVE(422)
  CONTENT_SENSITIVE: { title: "内容包含敏感信息,无法生成", hint: "请修改输入后重试" },
  // 1003 api 已加(平台代客渠道客户须填原因),console 快照此前未同步
  IMPERSONATE_REASON_REQUIRED: { title: "请填写代客原因", hint: "至少 4 个字" },
  VIDEO_FROM_POSTER_INVALID: { title: "海报来源无效", hint: REFRESH_HINT },
  VIDEO_CALLBACK_UNAUTHORIZED: { title: "回调鉴权失败" },
  VIDEO_DRAMA_CHARACTER_NOT_FOUND: { title: "角色不存在" },
  VIDEO_DRAMA_CHARACTER_NAME_DUPLICATE: { title: "角色名重复" },
  VIDEO_DRAMA_CHARACTER_REF_REQUIRED: { title: "缺少角色参考图" },
  VIDEO_DRAMA_CHARACTER_REF_TOO_MANY: { title: "角色参考图太多" },
  VIDEO_DRAMA_STORYBOARD_NOT_FOUND: { title: "分镜不存在" },
  VIDEO_DRAMA_STORYBOARD_NOT_EDITABLE: { title: "分镜现在不能编辑" },
  VIDEO_DRAMA_STORYBOARD_NOT_REGENERABLE: { title: "分镜现在不能重新生成" },
  VIDEO_DRAMA_STORYBOARD_FORBIDDEN: { title: "无权操作该分镜" },
  VIDEO_DRAMA_INVALID_IDEA: { title: "剧情梗概太短" },
  VIDEO_BGM_NOT_FOUND: { title: "背景音乐不存在" },
  VIDEO_BGM_INVALID_MIME: { title: "音乐格式不支持" },
  VIDEO_RECONCAT_MISSING_SHOT: { title: "重新合成缺少镜头", hint: "请先重出缺失的镜头" },

  // ── 真人出镜 ──
  REAL_PERSON_NOT_VERIFIED: { title: "出镜人还没完成认证" },
  VENDOR_ASSET_NOT_READY: { title: "人物素材还在准备" },
  VENDOR_ASSET_REJECTED: { title: "素材无法用于生成" },
  VENDOR_SUBMIT_TIMEOUT: { title: "提交超时" },
  VIDEO_SWAP_REF_REQUIRED: { title: "缺少参考视频" },
  VIDEO_SWAP_REQUIRES_REAL_PERSON: { title: "换我出镜需要真人人物" },
  SWAP_DURATION_EXCEEDS_TIER: { title: "参考视频太长" },
  REAL_PERSON_CAST_EMPTY: { title: "没有选出镜人" },
  FRAME_AND_REFERENCE_EXCLUSIVE: { title: "首帧图和参考图只能二选一" },
  VIDEO_VOICE_PROFILE_INVALID: { title: "所选声音不可用" },
  VIDEO_IDENTITY_REF_MODE_INVALID: { title: "身份参考档位有误", hint: REFRESH_HINT },
  REAL_PERSON_IDENTITY_VIDEO_MISSING: { title: "缺少身份视频" },
  VIDEO_TONE_REGISTER_INVALID: { title: "视频调性档有误", hint: REFRESH_HINT },

  // ── 分身舱(数字人)──
  DH_MODULE_DISABLED: { title: "分身舱未开通", hint: "请联系管理员开通" },
  DH_QUOTA_EXCEEDED: { title: "本月出片量已用完" },
  DH_QUOTA_EXHAUSTED: { title: "积分不足", hint: RECHARGE_HINT },
  DH_CONSENT_REQUIRED: { title: "需要先勾选授权承诺" },
  DH_PHOTO_UPLOAD_DISABLED: { title: "真人照片出片已关闭" },
  DH_TASK_NOT_FOUND: { title: "分身任务不存在", hint: "可能已被删除,刷新列表看看" },
  DH_TOO_MANY_IN_FLIGHT: { title: "排队中的分身任务已达上限" },
  DH_AI_SERVICE_DOWN: { title: "AI 服务暂不可用", hint: RETRY_LATER },
  DH_INVALID_PARAMS: { title: "分身参数有误" },
  DH_VOICE_QUOTA_EXCEEDED: { title: "声音数量已达上限" },
  DH_VOICE_NOT_FOUND: { title: "声音不存在" },
  DH_VOICE_FORBIDDEN: { title: "无权使用该声音" },
  DH_VOICE_CONSENT_REQUIRED: { title: "需要先勾选本人授权" },
  DH_VOICE_INVALID: { title: "声音样本不合格" },
  DH_VOICE_PARAM_INVALID: { title: "声音参数不支持" },
  DH_AVATAR_NOT_FOUND: { title: "形象不存在" },
  DH_AVATAR_FORBIDDEN: { title: "无权使用该形象" },
  DH_AVATAR_CONSENT_REQUIRED: { title: "需要先勾选肖像授权" },
  DH_AVATAR_INVALID: { title: "形象照不合格" },
  DH_AVATAR_LIMIT_EXCEEDED: { title: "形象数量已达上限" },
  DH_SHOWCASE_NOT_FOUND: { title: "样片不存在" },
  DH_SHOWCASE_INVALID_STATE: { title: "样片当前状态不允许此操作" },

  // ── 新闻舱 ──
  REPORTER_DRAFT_NOT_FOUND: { title: "草稿不存在" },
  REPORTER_ARTICLE_NOT_FOUND: { title: "文章不存在" },
  REPORTER_VERSION_NOT_FOUND: { title: "文章版本不存在" },
  REPORTER_DRAFT_BUSY: { title: "文章正在生成中" },
  REPORTER_EXPORT_GATED: { title: "导出前还需预检" },
  REPORTER_INVALID_PARAMS: { title: "写作参数有误" },

  // ── 建站舱 ──
  WEBSITE_UPSTREAM_UNAVAILABLE: { title: "建站服务暂不可用", hint: RETRY_LATER },
  WEBSITE_FIELD_UNKNOWN: { title: "有字段对不上站点模板" },
  TEMPLATE_BRAND_LEAK: { title: "还有模板原企业的名字" },
  TEMPLATE_PRODUCTS_NOT_SYNCED: { title: "产品还没同步" },

  // ── OEM 渠道(登录绑定域名,A-P2)──
  // 租户账号从别的渠道域名登录:不回显正确域名(后端同口径,防探出渠道域名清单)。
  PARTNER_DOMAIN_MISMATCH: { title: "请从贵司专属登录地址登录" },
  // 所属渠道商停用 / 解约:管理员与其租户都拒登。
  PARTNER_SUSPENDED: { title: "服务已暂停", hint: "请联系您的服务商" },
  PARTNER_NOT_FOUND: { title: "该地址未开通或已停用", hint: "请联系您的服务商" },

  // ── OEM 第二批:增值舱 / 舱门 / 渠道权限 / 月结撤销 / 自有域名开关 ──
  // 描述走后端原文(带具体套餐码 / 舱名),标题只说发生了什么。
  MODULE_NOT_OWNED: { title: "该舱未开通", hint: "不在套餐内且未购买,请用「赠送增值舱」或先加购" },
  ADDON_NOT_ELIGIBLE: { title: "当前套餐不能加购该增值舱" },
  ADDON_NOT_SELLABLE: {
    title: "有增值舱不可售",
    hint: "已停售或不在可售范围内,请调整勾选后再保存",
  },
  ADDON_ALREADY_ACTIVE: { title: "该增值舱已在有效期内" },
  ADDON_TERM_INVALID: { title: "当前订阅状态不能加购增值舱" },
  PLAN_NOT_SELLABLE: { title: "有套餐不可售", hint: "已停用或为 0 元 / 试用套餐,请去掉后再保存" },
  CHARGE_MODE_REQUIRED: { title: "请选择扣渠道钱包或平台赠送" },
  PARTNER_ROLE_FORBIDDEN: { title: "当前角色无权操作", hint: "请联系渠道管理员" },
  // 渠道子账号(A7 专用码):改 / 停用 / 删 / 重置自己;改完没有启用中的 OWNER;子账号满 20 个。
  PARTNER_ACCOUNT_SELF_FORBIDDEN: {
    title: "不能对自己的账号做这个操作",
    hint: "改自己的密码请到「个人资料」",
  },
  PARTNER_OWNER_REQUIRED: {
    title: "至少要保留 1 个启用中的渠道管理员",
    hint: "请先把其他账号设为渠道管理员",
  },
  PARTNER_ACCOUNT_LIMIT: {
    title: "账号数已达上限",
    hint: "最多 20 个(含停用),请先删除不用的账号",
  },
  SETTLEMENT_REVOKE_BLOCKED: {
    title: "暂时不能撤销",
    hint: "后续月份的对账单已确认,请先撤销后面的月份",
  },
  CUSTOM_DOMAIN_DISABLED: { title: "自有域名功能暂未开放", hint: "请先使用平台子域名" },

  // ── OEM 私有化 License(W4-private)──
  // 只读闸 423:授权过期 / 未安装 / 离线超过宽限期,写操作一律被拦(全站另有顶部横幅)。
  LICENSE_READONLY: { title: "系统处于只读状态", hint: "授权已过期或未安装,请联系服务商" },
  // 开户撞 license 的 max_tenants。
  LICENSE_TENANT_LIMIT: { title: "租户数已达授权上限", hint: "请联系服务商扩容" },
  // 网关 / 心跳部署身份(key 签名)校验失败 —— 平台侧给私有化实例的回包,一般不会到 console 用户眼前。
  LICENSE_AUTH_FAILED: { title: "部署身份校验失败", hint: "请核对部署 key 是否已轮换" },

  // ── 首登强制改密(0930)──
  // 待改密会话调了改密 / 登出 / 读 /me 之外的接口;httpClient 同时置位,整站切到改密页。
  PASSWORD_CHANGE_REQUIRED: { title: "请先修改初始密码", hint: "设置新密码后再继续使用" },

  // ── 兜底 ──
  INTERNAL_SERVER_ERROR: { title: "服务出了点问题", hint: RETRY_LATER },
};

/**
 * 后端**不在 ErrorCode 枚举里、而是字面量发出**的码(GlobalExceptionHandler 里 `ErrorResponse.of("…")`)。
 * 快照只从 ErrorCode.java 抽,这几条单列,测试断言它们与快照不重叠。
 */
export const SERVER_LITERAL_ERROR_COPY: Record<string, ErrorCopy> = {
  CONFIG_ERROR: { title: "配置有误", hint: "请联系管理员检查配置" },
  BILLING_VERIFICATION_FAILED: { title: "计费校验没通过", hint: REFRESH_HINT },
};

/** console 自己造的码(不是后端发的):边缘 nginx / 断网 / 超时 / 网关。 */
export const CLIENT_ERROR_COPY: Record<string, ErrorCopy> = {
  // httpClient:nginx 回 HTML 413(描述里是 PAYLOAD_TOO_LARGE_MESSAGE)
  PAYLOAD_TOO_LARGE: { title: "文件太大" },
  // httpClient:网关回 HTML 502/503(backend 重启或挂了)
  GATEWAY_ERROR: { title: "服务暂时连不上", hint: "通常几十秒内恢复,请稍后重试" },
  // httpClient:网关回 HTML 504(backend 还在处理,网关先断了)
  GATEWAY_TIMEOUT: { title: "服务器响应超时" },
  NETWORK_ERROR: { title: "网络连接断开了" },
  REQUEST_TIMEOUT: { title: "网络没回应" },
  // 信封里没带 code
  UNKNOWN: { title: "操作没有成功" },
};

export const UNKNOWN_ERROR_TITLE = "操作没有成功";

export interface ErrorToast {
  title: string;
  description: string;
  variant: "destructive";
}

export interface DescribeOptions {
  /**
   * 调用点语境标题(如「下载失败」)。只在**没有映射**时顶替「操作没有成功」——
   * 有映射的码(积分不足 / 并发已满…)比语境标题更说得清原因。
   */
  fallbackTitle?: string;
  /**
   * 描述里挡住异常原文(**默认开**,0926 第六批 R:由兜底专用改为全站默认)。
   * · ApiError:后端 message 若带内部地址 / 异常类名 / 堆栈痕迹(见 looksLikeInternalDetail)→ 换通用人话,
   *   码与排查号照旧留末行;人话原文(积分不足带真数字等)照常展示。
   * · 非 ApiError:前端自己 `throw new Error("中文人话")` 的照常展示;无中文(TypeError / JSON 解析 /
   *   英文异常)或带内部细节的 → 换通用人话。
   * 只有「确实要把后端/异常原文原样给人看」的调用点才显式传 false(目前全站无此类调用点)。
   */
  hideRawDetail?: boolean;
  /**
   * 原文被藏掉(或根本没有可读原文)时替代 {@link RAW_DETAIL_HIDDEN_DESC} 的调用点专用说明
   * (如「这次会从零画…」)。只影响「藏掉」那一支;后端人话原文照常展示。0926 第八批 Z3。
   */
  hiddenDescription?: string;
}

/** 兜底描述:原文被藏掉时给用户的话。 */
export const RAW_DETAIL_HIDDEN_DESC = "请稍后重试;若一直失败,请把排查号发给我们";

/**
 * 判一段报错文本是否带「不该给用户看的」内部细节:URL、内网主机:端口、IP、Java/Python 异常类名、
 * 堆栈行、底层 I/O 字样。宁可多判(多判只是换成通用人话,少判会把内部地址怼给用户)。
 */
const INTERNAL_DETAIL_PATTERNS: RegExp[] = [
  /https?:\/\//i,
  /\b\d{1,3}(?:\.\d{1,3}){3}\b/, // IPv4
  /\b(?:localhost|[a-z][\w-]*):\d{2,5}\b/i, // host:port(gscq-ai:8000 / localhost:8081)
  /\b(?:java|javax|org|com|io|reactor)\.[a-z_]\w*\./, // 包名(java.net.… / org.springframework.…)
  /\b\w+(?:Exception|Error):/, // 异常类名: 原文
  /\bat [\w$.]+\(/, // 堆栈行
  /Traceback \(most recent call last\)/,
  /\b(?:I\/O error|Connection refused|Connect timed out|Read timed out|errno)\b/i,
];

/** 前端自抛的报错是否人话:含中文且不带内部细节。 */
function isHumanClientMessage(msg: string): boolean {
  return /[\u4e00-\u9fff]/.test(msg) && !looksLikeInternalDetail(msg);
}

export function looksLikeInternalDetail(text: string | undefined | null): boolean {
  if (!text) return false;
  return INTERNAL_DETAIL_PATTERNS.some((re) => re.test(text));
}

function lookupCopy(code: string): ErrorCopy | undefined {
  return API_ERROR_COPY[code] ?? SERVER_LITERAL_ERROR_COPY[code] ?? CLIENT_ERROR_COPY[code];
}

function joinDesc(message: string, hint?: string): string {
  const m = message.trim();
  if (!hint) return m;
  if (!m) return hint;
  // 后端原文已含同一句下一步(如「…请充值后再试」)时不重复接
  if (m.includes(hint)) return m;
  return `${m} · ${hint}`;
}

/** 描述末行小字:错误码 + 排查号,给报障用。只在有东西时加。 */
function diagLine(code: string | undefined, traceId: string | undefined): string {
  const parts: string[] = [];
  if (code) parts.push(`错误码 ${code}`);
  if (traceId) parts.push(`排查号 ${traceId}`);
  return parts.join(" · ");
}

function withDiag(desc: string, code: string | undefined, traceId: string | undefined): string {
  const line = diagLine(code, traceId);
  if (!line) return desc;
  return desc ? `${desc}\n${line}` : line;
}

/**
 * 把任意 throwable 转成人话 toast。**全站失败提示的唯一口径**。
 *
 *     toast(errorToast(e));                               // 标题按码
 *     toast(errorToast(e, { fallbackTitle: "下载失败" })); // 未映射时用语境标题
 */
export function errorToast(e: unknown, opts: DescribeOptions = {}): ErrorToast {
  // ★OEM 0929(C-C):全站失败提示出口统一脱敏(厂商 / 模型名 → 中性词);
  //   超管非代客态 vendorMask reveal 时原样。errorText / errorTitle 都经这里,不用再各包一层。
  const t = buildErrorToast(e, opts);
  // ★1004 T10:描述再过一遍内部术语清洗(后端原文「无效的 sessionId」这类字段名 / 内部标记不直出)。
  return {
    ...t,
    title: maskVendor(t.title),
    description: humanizeInternalTerms(maskVendor(t.description)),
  };
}

function buildErrorToast(e: unknown, opts: DescribeOptions): ErrorToast {
  const fallbackTitle = opts.fallbackTitle ?? UNKNOWN_ERROR_TITLE;

  if (e instanceof RequestTimeoutError) {
    return {
      title: CLIENT_ERROR_COPY.REQUEST_TIMEOUT.title,
      description: withDiag(
        "等了太久没有回应,可能是网络不稳。操作可能仍在后台进行,刷新看看再决定是否重试",
        undefined,
        e.traceId
      ),
      variant: "destructive",
    };
  }
  if (e instanceof NetworkError) {
    return {
      title: CLIENT_ERROR_COPY.NETWORK_ERROR.title,
      description: withDiag(e.message, undefined, e.traceId),
      variant: "destructive",
    };
  }
  if (e instanceof ApiError) {
    // OEM 第二批 C4:MODULE_NOT_ENABLED 带 reason=PAUSED = 已拥有、被服务商暂停,标题不能说「未开通」。
    //   后端原文已含「请联系服务商」,不再接 hint。缺 reason(部分 service 自抛)照旧按「未开通」。
    const copy =
      e.errorCode === "MODULE_NOT_ENABLED" && moduleBlockReasonFromDetails(e.details) === "PAUSED"
        ? { title: MODULE_PAUSED_TITLE }
        : lookupCopy(e.errorCode);
    const rawHidden = opts.hideRawDetail !== false && looksLikeInternalDetail(e.message);
    if (rawHidden) {
      // 原文带内部细节:标题照旧按码,描述换通用人话,码与排查号留末行
      return {
        title: copy?.title ?? fallbackTitle,
        description: withDiag(
          joinDesc(opts.hiddenDescription ?? RAW_DETAIL_HIDDEN_DESC, copy?.hint),
          copy ? undefined : e.errorCode,
          e.traceId
        ),
        variant: "destructive",
      };
    }
    if (copy) {
      // 已映射:码本身不进标题;排查号照旧亮出来
      return {
        title: copy.title,
        description: withDiag(joinDesc(e.message, copy.hint), undefined, e.traceId),
        variant: "destructive",
      };
    }
    // 未映射:标题不放裸码,码与排查号进描述末行
    return {
      title: fallbackTitle,
      description: withDiag(e.message, e.errorCode, e.traceId),
      variant: "destructive",
    };
  }
  const msg =
    e instanceof Error && e.message ? e.message : (opts.hiddenDescription ?? "未知错误,请稍后重试");
  if (opts.hideRawDetail !== false && !isHumanClientMessage(msg)) {
    // 前端自己抛的英文异常(TypeError / 解析失败 / bug)或带内部细节:原文对用户没有意义
    return {
      title: fallbackTitle,
      description: opts.hiddenDescription ?? RAW_DETAIL_HIDDEN_DESC,
      variant: "destructive",
    };
  }
  return { title: fallbackTitle, description: msg, variant: "destructive" };
}

/** 只要标题(给 setError / 内联错误条这类不走 toast 的地方)。 */
export function errorTitle(e: unknown, opts: DescribeOptions = {}): string {
  return errorToast(e, opts).title;
}

/**
 * 一行内联错误文案(给 setError / 内联错误条 / H5 Toast 这类只放一段字的地方;0926 第八批 Z3)。
 * 与 {@link errorToast} 同口径:内部细节照样藏、码与排查号照样带(换行并成「 · 」)。
 * 原文被藏掉时描述只剩通用人话,前面补上标题(语境标题或按码标题),免得只看到一句「请稍后重试」。
 *
 *     setError(errorText(e, { fallbackTitle: "加载失败" }));
 */
export function errorText(e: unknown, opts: DescribeOptions = {}): string {
  const t = errorToast(e, opts);
  const desc = t.description.replace(/\s*\n\s*/g, " · ").trim();
  if (!desc) return t.title;
  if (desc.startsWith(RAW_DETAIL_HIDDEN_DESC)) return `${t.title}:${desc}`;
  return desc;
}
